import { useMemo } from 'react';

import { FieldValues, Path, useController, UseControllerProps } from 'react-hook-form';

import { AsyncAutocomplete, EditableRowsTable } from 'components';
import strings from 'constants/index';
import { useSports } from 'contexts/SportContext';
import { IdColumn, NameColumn } from 'utils/table.utils';

interface WithSportValues extends FieldValues {
  sports: Array<Entity & EditableItem>;
}

export function SportsFormField<T extends WithSportValues>({
  control,
  required,
}: UseControllerProps<T> & { required?: boolean }) {
  const {
    field: { onChange, value: sports },
  } = useController<T>({
    name: 'sports' as Path<T>,
    control,
    rules: {
      required: { value: required, message: strings.field_is_required },
    },
  });

  const { sports: sportsSource } = useSports();

  const defaultValue = useMemo(
    () => sports.find((el: Entity & EditableItem) => el.active),
    [sports],
  );

  const columns = [
    IdColumn,
    {
      ...NameColumn,
      label: strings.sport,
      editControl: (
        <AsyncAutocomplete
          dataSource={sportsSource}
          label={strings.sport_search}
          defaultValue={defaultValue?.id ? defaultValue : null}
          onChange={item => {
            if (item && !sports.some(el => el.id === item.id)) {
              const itemIdx = sports.findIndex(el => el.active);
              if (itemIdx !== -1) {
                const newItem = { ...sports[itemIdx], ...item };
                const newItems = [...sports];
                newItems[itemIdx] = newItem;
                onChange(newItems);
              }
            }
          }}
          size="small"
        />
      ),
    },
  ];

  return <EditableRowsTable items={sports} columns={columns} onSave={onChange} />;
}
